import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import API_URL from "../../config/ApiUrl";

/**
 * Fetch item transactions with filters and pagination
 * @param {Object} payload - { itemId, transactionType, status, fromDate, toDate, pageNumber, pageSize }
 */
export const getItemTransactions = createAsyncThunk(
  "ItemTransaction/get",
  async (payload, { rejectWithValue }) => {
    try {
      const { itemId, ...filters } = payload;

      // Filter out null/undefined/empty values
      const filteredPayload = Object.fromEntries(
        Object.entries(filters).filter(
          ([, value]) => value !== null && value !== undefined && value !== ""
        )
      );

      const params = new URLSearchParams();
      for (const [key, value] of Object.entries(filteredPayload)) {
        params.append(key, value);
      }

      const queryString = params.toString();
      const url = queryString
        ? `${API_URL.ITEM.TRANSACTIONS(itemId)}?${queryString}`
        : API_URL.ITEM.TRANSACTIONS(itemId);

      const { data } = await axios.get(url);

      return {
        items: data.data.list || [],
        pagination: {
          current: data.data.pageNumber || 1,
          pageSize: data.data.pageSize || 20,
          total: data.data.totalRecords || 0,
          totalPages: data.data.totalPages || 0,
        },
      };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

const itemTransactionSlice = createSlice({
  name: "itemTransaction",
  initialState: {
    transaction_list: [],
    loading: false,
    error: null,
    pagination: {
      current: 1,
      pageSize: 20,
      total: 0,
      totalPages: 0,
    },
    filters: {
      transactionType: "",
      status: "",
      fromDate: null,
      toDate: null,
    },
  },
  reducers: {
    setFilters(state, action) {
      state.filters = { ...state.filters, ...action.payload };
    },
    resetFilters(state) {
      state.filters = {
        transactionType: "",
        status: "",
        fromDate: null,
        toDate: null,
      };
    },
    resetTransactions(state) {
      state.transaction_list = [];
      state.loading = false;
      state.error = null;
      state.pagination = {
        current: 1,
        pageSize: 20,
        total: 0,
        totalPages: 0,
      };
      state.filters = {
        transactionType: "",
        status: "",
        fromDate: null,
        toDate: null,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getItemTransactions.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.transaction_list = []; // Clear old data when new fetch starts
      })
      .addCase(getItemTransactions.fulfilled, (state, action) => {
        state.transaction_list = action.payload.items;
        state.pagination = action.payload.pagination;
        state.loading = false;
      })
      .addCase(getItemTransactions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.transaction_list = [];
      });
  },
});

export const { setFilters, resetFilters, resetTransactions } =
  itemTransactionSlice.actions;
export default itemTransactionSlice.reducer;
